import React, { useState } from 'react';
import Lightbox from '../components/Lightbox';
import MediaCard from '../components/MediaCard';
import ModelViewer from '../components/ModelViewer';
import { useMedia } from '../hooks/useMedia';
import { PageHeader, EmptyChannel } from './Animation';
import { iconFor } from '../lib/icons';
import { Maximize2 } from 'lucide-react';

export default function Modelling({ category, input, fallback }) {
  const [selected, setSelected] = useState(null);
  const { items: remote } = useMedia(category.key);
  const works = remote === null ? [] : remote.length ? remote : (fallback || []);

  return (
    <div className="page-in w-full max-w-[1800px] mx-auto px-4 md:px-6 py-4 md:py-6 lg:py-8" data-testid={`page-${category.key}`}>
      <PageHeader input={input} title={category.name} icon={iconFor(category.icon)} />
      {remote && !works.length && <EmptyChannel />}

      <div className="grid gap-4 md:gap-5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 460px), 600px))', justifyContent: 'center' }}>
        {works.map((item, i) => {
          // Photos/videos dropped into a 3D channel render as normal cards.
          if (item.type === 'image' || item.type === 'video') {
            return <MediaCard key={item.id} item={item} kind={item.type} onOpen={setSelected} index={i} />;
          }
          const isInLightbox = selected?.id === item.id;
          return (
            <div key={item.id} className="tv-card" style={{ animationDelay: `${i * 80}ms` }}>
              <div className="relative">
                {/*
                 * Three.js can't share one scene across two Canvases. While the
                 * lightbox is showing this item, we re-mount the card-view as a
                 * blank "off-air" placeholder so the scene only ever lives in
                 * the lightbox. When the lightbox closes, key flips back and
                 * the card-view reloads fresh.
                 */}
                {isInLightbox ? (
                  <div className="relative w-full h-[280px] flex items-center justify-center bg-[radial-gradient(ellipse_at_center,#2a1608_0%,#0f0704_75%)]">
                    <div className="font-osd text-[#f5a623] text-sm tracking-widest opacity-70">// SIGNAL ROUTED TO MAIN OUTPUT</div>
                    <div className="absolute inset-0 pointer-events-none" style={{
                      background: 'repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0 2px, rgba(0,0,0,0.22) 3px 4px)',
                      mixBlendMode: 'multiply'
                    }} />
                  </div>
                ) : (
                  <ModelViewer key={`card-${item.id}`} item={item} onExpand={() => setSelected(item)} />
                )}
                <button
                  onClick={() => setSelected(item)}
                  onPointerDown={(e) => e.stopPropagation()}
                  data-testid={`model-expand-${item.id}`}
                  className="absolute top-3 right-3 font-osd text-xs bg-[#1a0e05]/90 border-2 border-[#f5a623] text-[#f5a623] hover:bg-[#f5a623] hover:text-[#1a0e05] px-3 py-1.5 rounded transition-colors flex items-center gap-1.5">
                  <Maximize2 className="w-3.5 h-3.5" /> EXPAND
                </button>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-2xl text-[#ffe9b8] chroma">{item.title}</h3>
                  <span className="font-osd text-xs text-[#f5a623] whitespace-nowrap mt-1.5">{formatDate(item.date)}</span>
                </div>
                {item.software && (
                  <div className="font-osd text-xs text-[#8b6a3e] mt-1 tracking-wider">{item.software}</div>
                )}
                <p className="font-body text-sm text-[#a88552] mt-3 leading-relaxed">{item.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      {!remote?.length && works.length > 0 && (
        <div className="mt-10 border-2 border-dashed border-[#5a3418] rounded-lg p-5 font-osd text-sm text-[#8b6a3e] bg-[#1a0e05]/40">
          // PLACEHOLDER PRIMITIVES ARE DISPLAYED. UPLOAD .GLB / .GLTF VIA THE ADMIN DESK AT /ADMIN TO REPLACE THESE UNITS.
        </div>
      )}

      <Lightbox item={selected} kind="3d" onClose={() => setSelected(null)} />
    </div>
  );
}

function formatDate(d) {
  try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' }).toUpperCase(); }
  catch { return d; }
}
